import { IconAttributeTraveller } from '@components/icons/attribute-traveller';
import { IconEmail } from '@components/icons/email';
import { TextField } from '@components/text-field';
import { getScopedTranslate } from '@locale/translate';
import { asteriskLabel } from '@utils/strings';

import { FieldDropzone } from './field-dropzone';

import { conditionsTab } from '../conditions-tab';
import { ConfirmationSwitch } from '../confirmation-switch';
import { useFormBuilderContext } from '../context';
import { getBuilderDisplayDefault } from '../default-value-input';
import { DefaultValueMapping, DefaultValueMappingDisplay } from '../default-value-mapping';
import { DescriptionInput } from '../description-input';
import { FieldEdit } from '../field-edit';
import { FieldConfig } from '../field-registry';
import { FieldSidebarLabel } from '../fields-sidebar';
import { RequiredSwitch } from '../required-switch';

type Config = FieldConfig<'input-email'>;

const Display: Config['Display'] = ({ field }) => {
  const displayDefault = getBuilderDisplayDefault(field);
  return (
    <DefaultValueMappingDisplay field={field}>
      <TextField
        key={displayDefault ?? 'empty'}
        label={asteriskLabel(field.label, field.isRequired)}
        description={field.description}
        placeholder={field.placeholder}
        labelIcon={field.companyAttributeId ? <IconAttributeTraveller size={16} /> : undefined}
        defaultValue={displayDefault}
      />
    </DefaultValueMappingDisplay>
  );
};

const FormBuilderEmail: Config['Component'] = ({ field, dragHandle }) => {
  const actions = useFormBuilderContext().store.actions;

  return (
    <FieldDropzone field={field}>
      <FieldEdit
        field={field}
        dragHandle={dragHandle}
        display={<Display field={field} />}
        edit={
          <>
            <TextField
              onChange={actions.createChangeHandler(field, 'label')}
              defaultValue={field.label}
              label={t('label')}
              maxChars={255}
            />
            <DescriptionInput field={field} />
            <TextField
              onChange={actions.createChangeHandler(field, 'placeholder')}
              defaultValue={field.placeholder}
              label={t('placeholder')}
            />
            <RequiredSwitch field={field} />
            {field.isRequired && (
              <ConfirmationSwitch
                onChange={actions.createChangeHandler(field, 'hasConfirmation')}
                value={field.hasConfirmation}
              />
            )}

            {field.hasConfirmation && field.isRequired && (
              <TextField
                onChange={actions.createChangeHandler(field, 'confirmLabel')}
                defaultValue={field.confirmLabel}
                label={t('confirmationLabel')}
              />
            )}
            <DefaultValueMapping field={field} />
          </>
        }
      />
    </FieldDropzone>
  );
};

const { t } = getScopedTranslate({
  en: {
    title: 'Email address',
    sidebarTitle: 'Email address',
    label: 'Label',
    confirmationLabel: 'Confirmation label',
    placeholder: 'Placeholder text',
  },
});

export const emailConfig: Config = {
  kind: 'input-email',
  title: t('title'),
  sidebarTitle: t('sidebarTitle'),
  group: 'inputs',
  propertiesTabs: [conditionsTab],
  Display,
  TreeDisplay: ({ field }) => <FieldSidebarLabel label={field.label} />,
  Component: FormBuilderEmail,
  Icon: IconEmail,
};
